/**
 * Dexie-Datenschicht für die clientseitige Dateihistorie.
 *
 * Speichert **alles in IndexedDB** (via Dexie) – Metadaten und Datei-Bytes. Bewusst auf zwei
 * Tabellen aufgeteilt:
 *  - `entries` – leichte Metadaten je Eintrag (wird beim Start vollständig in den Store geladen).
 *  - `blobs`   – die eigentlichen Bytes als `Blob`, separat per Id abgerufen, damit nicht alle
 *               Dateien gleichzeitig im Speicher/Reaktivitätssystem liegen.
 *
 * Überlebt Reload und den serverseitigen ~24h-Cleanup. IndexedDB ist praktisch überall
 * verfügbar (anders als OPFS).
 *
 * Modell: Parent/Child als **lineare Kette**. Ein `upload`-Root pro Bearbeitungslinie;
 * `transposed`/`shifted`/`converted`/`edited` zeigen über `parentId` auf ihren **unmittelbaren
 * Vorgänger** (den Stand, aus dem sie hervorgingen) – NICHT pauschal auf den Root. So lässt sich
 * die Bearbeitungsfolge rückwärts ablaufen ("Schritt zurück"/Undo, siehe
 * `useFileHistoryStore.loadPreviousState`). Alle teilen `sourceFileId` als Gruppierungsschlüssel.
 * `sourceFileId` ist die clientseitige **Lineage-Id** (NICHT die Server-`file_id`) – sie bleibt
 * über Transpositionen, Konvertierungen und manuelle Edits stabil, sodass alles zur selben
 * Originaldatei gruppiert. Die Server-`file_id` wird in der History nicht benötigt (Reuse lädt
 * die gespeicherten Bytes neu hoch).
 */

import Dexie, {type Table} from 'dexie';
import type {AnalysisResult, ConversionSettings} from '@/generated';
import type {ChordInferenceStatus} from '@/composables/musicXmlDom';

export type HistoryKind = 'upload' | 'transposed' | 'shifted' | 'merged' | 'mode-changed' | 'converted' | 'edited';

/** Kompakte, für die Listenanzeige gedachte Auszüge aus {@link AnalysisResult}. */
export interface AnalysisSummary {
    key?: string | null;
    timeSignature?: string | null;
    numMeasures?: number;
    numNotes?: number;
    unmappableCount?: number;
}

/** Leichte Metadaten eines Historieneintrags (ohne Bytes). */
export interface HistoryEntry {
    /** Eindeutige Id (crypto.randomUUID); zugleich Schlüssel der Blob-Tabelle. */
    id: string;
    kind: HistoryKind;
    /** Anzeigename der Datei. */
    filename: string;
    /** Optionaler, vom Nutzer vergebener Name (überschreibt `filename` in der Anzeige). */
    name?: string | null;
    /** MIME-Typ der gespeicherten Bytes. */
    mimeType: string;
    /** Dateiendung/-typ aus der API-Antwort (z. B. ".musicxml"). */
    fileType?: string | null;
    /** Größe der Bytes in Byte. */
    size: number;
    /** Erstellungszeitpunkt (ms seit Epoch). */
    createdAt: number;
    /** Id des unmittelbaren Vorgänger-Stands (lineare Kette); `null`/`undefined` beim Root-Upload. */
    parentId?: string | null;
    /** Gruppierungsschlüssel = clientseitige Lineage-Id (stabil über Transpose/Convert/Edit). */
    sourceFileId: string;
    /** Bei `transposed`/`converted`: kumulierte Transposition in Halbtönen. */
    transposedSemitones?: number;
    /** Snapshot der (flüchtigen) Konvertierungs-Id – NIE für Reuse verlässlich. */
    conversionId?: string | null;
    /** Tonart, sofern bekannt (rein informativ). */
    key?: string | null;
    /** Kompakte Analyse-Zusammenfassung für die Listenanzeige (volle Analyse: {@link HistoryAnalysis}). */
    analysisSummary?: AnalysisSummary | null;
    /** Für die Konvertierung verwendete Settings (bei `converted`-Einträgen). */
    settings?: ConversionSettings | null;
    /**
     * Zustand der automatischen Akkorderkennung für **diesen** Stand, beim Erfassen aus dem
     * MusicXML abgeleitet (`chordInferenceStatus`). Unindiziertes Feld → **kein** Dexie-Versions-
     * Bump nötig; Altbestand bleibt gültig (`undefined` → keine Anzeige).
     */
    chordInference?: ChordInferenceStatus | null;
    /** SHA-256 der gespeicherten Bytes (hex) – für Duplikat-Erkennung. */
    contentHash?: string;
    /**
     * Gruppierung mehrerer Movements aus **einem** mehrteiligen PDF-Upload (opakes Client-Label).
     * Alle Movements desselben Uploads teilen dieselbe `documentId`; jedes Movement hat aber eine
     * eigene `sourceFileId` (Lineage). Bei Einzeldatei-Uploads `undefined`.
     */
    documentId?: string | null;
    /** 0-basierter Movement-Index innerhalb des Dokuments (nur bei mehrteiligen PDFs gesetzt). */
    movementIndex?: number | null;
}

/** Bytes-Datensatz, per `id` mit {@link HistoryEntry} verknüpft. */
export interface HistoryBlob {
    id: string;
    blob: Blob;
}

/** Vollständige Analyse-Response, per `id` mit {@link HistoryEntry} verknüpft (music_xml entfernt). */
export interface HistoryAnalysis {
    id: string;
    analysis: AnalysisResult;
}

export class HistoryDatabase extends Dexie {
    entries!: Table<HistoryEntry, string>;
    blobs!: Table<HistoryBlob, string>;
    analyses!: Table<HistoryAnalysis, string>;

    constructor() {
        super('music-history');
        this.version(1).stores({
            // Nur indizierte Felder; übrige Felder werden unindiziert gespeichert.
            entries: 'id, kind, sourceFileId, parentId, createdAt, [sourceFileId+createdAt]',
            blobs: 'id',
            analyses: 'id',
        });
        // v2: `documentId`/`movementIndex` für mehrteilige PDF-Uploads (Movements-Gruppierung).
        // Neue optionale, indizierte Spalten – bestehende Einträge bleiben gültig (Werte undefined).
        this.version(2).stores({
            entries:
                'id, kind, sourceFileId, parentId, createdAt, [sourceFileId+createdAt], ' +
                'documentId, [documentId+movementIndex]',
            blobs: 'id',
            analyses: 'id',
        });
    }
}

export const historyDb = new HistoryDatabase();
